import { createLogger, HttpLogTransport, defaultConfig, Logger } from "co-addin-logging-build";
import { WordLoggerContextProvider } from "./WordLoggerContextProvider";
import { store } from "../../redux/store/store";
import { selectBackendUrl } from "../../redux/feature/settings/configurations-slice";
import { axiosInstance } from "../../utils/axios";

const CO_SERVICE = "co-service";
const CO_CHANGES_TRACKER = "co-changes-tracker";
const LOGGING_ENDPOINT_PATH = "/ui/api/co2/logging";
const LOCALHOST_TRACKER_URL = "http://localhost:8088/co-changes-tracker";

let loggerCleanup: (() => void) | null = null;
let loggerInstance: Logger | null = null;

export function setLoggerCleanup(cleanup: () => void): void {
  loggerCleanup = cleanup;
}

export function getLoggerCleanup(): (() => void) | null {
  return loggerCleanup;
}

export function restoreConsole(): void {
  if (loggerCleanup) {
    loggerCleanup();
    loggerCleanup = null;
    loggerInstance = null;
  }
}

export function getLoggerInstance(): Logger | null {
  return loggerInstance;
}

export function isLoggerInitialized(): boolean {
  return loggerInstance !== null && loggerCleanup !== null;
}

export function getLoggerEndpoint(backendUrl: string): string {
  let url = backendUrl;
  if (url.indexOf(CO_SERVICE) !== -1) {
    url = url.replace(CO_SERVICE, CO_CHANGES_TRACKER);
  }
  if (url.indexOf("localhost") !== -1) {
    url = LOCALHOST_TRACKER_URL;
  }

  return url.concat(LOGGING_ENDPOINT_PATH);
}

export interface InitializeLoggerOptions {
  debugEnabled?: boolean | (() => boolean);
  maxRetries?: number;
  maxBufferSize?: number;
  flushInterval?: number;
}

export function initializeLogger(options: InitializeLoggerOptions = {}): Logger {
  if (loggerInstance) {
    restoreConsole();
  }

  const {
    debugEnabled = false,
    maxRetries = 3,
    maxBufferSize = defaultConfig.maxBufferSize,
    flushInterval = defaultConfig.flushInterval,
  } = options;

  const state = store.getState();
  const backendUrl = selectBackendUrl(state);
  const contextProvider = new WordLoggerContextProvider(store);
  const loggerEndpoint = getLoggerEndpoint(backendUrl);
  const transport = new HttpLogTransport({
    endpoint: loggerEndpoint,
    httpClient: axiosInstance,
    maxRetries,
  });

  const logger = createLogger({
    contextProvider,
    transport,
    config: {
      maxBufferSize,
      flushInterval,
      retryAttempts: maxRetries,
      debugEnabled,
    },
  });

  loggerCleanup = logger.installGlobally();
  loggerInstance = logger;

  console.log("3. ...Logger initialized :::", loggerInstance);

  return logger;
}

export function reinitializeLogger(options: InitializeLoggerOptions = {}): Logger {
  restoreConsole();
  return initializeLogger(options);
}
